import '@tamagui/core/reset.css'

import { data, useLocation, useNavigate } from 'react-router-dom'
import { cache, forwardRef, useCallback, useContext, useEffect, useState } from 'react'
import { listQuestionnaires, Questionnaire, QuestionnaireVersion, QuestionAuthoringStatus, QuestionSet, QuestionSetVersion, Question, QuestionVersion, QuestionType, QuestionEvidenceRequirement, QuestionnaireEntry, QuestionnaireEntryType, QuestionSetEntry, createQuestion, createQuestionSet, createQuestionVersion, createQuestionSetVersion, createQuestionnaire, createQuestionnaireVersion, createQuestionnaireEntry, listQuestionSets, listQuestions, deleteQuestionnaire, deleteQuestion, deleteQuestionSet, getQuestion, publishQuestionVersion, getQuestionSet, publishQuestionSetVersion, getQuestionnaire, publishQuestionnaireVersion, searchQuestionSets, searchQuestions } from '../remote/questionnaires'
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, Input, InputLabel, List, ListItem, ListSubheader, MenuItem, Modal, Paper, Popover, Popper, Select, Slide, Stack, TextField, Typography } from '@mui/material'
import { Add, Check, ChevronRightOutlined, Clear, Close, Create, CreateNewFolder, DeleteForever, Drafts, Edit, EditDocument, Error, ExpandMore, X } from '@mui/icons-material'
import { OrganizationContext } from '../contexts/OrganizationContext'
import { instantiateQuestion, instantiateQuestionSet, instantiateQuestionnaire, instantiateQuestionnaireEntry, instantiateQuestionSetVersionEntry, populateCacheWithQuestions, instantiateQuestionVersion, MakeQuestionDataRef, QuestionDataType, populateCacheWithQuestionSets, getQuestionSetChildrenMissingFromCache, retrieveQuestionData, instantiateQuestionSetVersion, generateNewLocalId, getQuestionnaireChildrenMissingFromCache, populateCacheWithQuestionnaires, instantiateQuestionnaireVersion, getQuestionnaireVersionUnpublishedChildren, UnpublishedChildren, publishAll, getQuestionSetVersionUnpublishedChildren, getQuestionChildrenMissingFromCache } from '../utils/questionnaire'
import { instantiateQuestionnaireCache, QuestionnaireDataCache } from '../utils/questionnaire'
import { saveQuestionnaireDataToBackend, SaveState } from '../utils/questionnaire'
import { TransitionProps } from '@mui/material/transitions'

export interface QuestionSearchProps {
	searchQuestions?: boolean
	searchQuestionSets?: boolean
	onQuestionSelected?: (q: Question) => void
	onQuestionSetSelected?: (qs: QuestionSet) => void
	cache?: QuestionnaireDataCache
	selectedIds?: string[]
}

export const QuestionSearch = (props: QuestionSearchProps) => {
	const [searchText, setSearchText] = useState<string>('')
	const [questionSearchResults, setQuestionSearchResults] = useState<Question[]>([])
	const [questionSetSearchResults, setQuestionSetSearchResults] = useState<QuestionSet[]>([])
	const [selected, setSelected] = useState(false)
	const [hovered, setHovered] = useState(false)
	const [searchingQuestions, setSearchingQuestions] = useState(false)
	const [searchingQuestionSets, setSearchingQuestionSets] = useState(false)
	const orgId = useContext(OrganizationContext)
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	if (!orgId) {
		return
	}

	useEffect(() => {
		if (props.searchQuestions) {
			setSearchingQuestions(true)
			searchQuestions({organization: orgId, searchText: searchText}, (q?: Question[], resp?, err?) => {
				setSearchingQuestions(false)
				if (q) {
					setQuestionSearchResults(q)
				}
			})
		}
	}, [orgId, searchText, setQuestionSearchResults])

	useEffect(() => {
		if (props.searchQuestionSets) {
			setSearchingQuestionSets(true)
			searchQuestionSets({organization: orgId, searchText: searchText}, (qs?: QuestionSet[], resp?, err?) => {
				setSearchingQuestionSets(false)
				if (qs) {
					setQuestionSetSearchResults(qs)
				}
			})
		}
	}, [orgId, searchText, setQuestionSetSearchResults])

	const id = selected ? 'question-search-popover' : undefined;

	return <>
		<TextField aria-describedby={id} label={<Typography variant='body1'>Search Questions..</Typography>} fullWidth type="search"
			onChange={(e) => setSearchText(e.target.value)}
			onFocus={(e) => {setAnchorEl(e.currentTarget); setSelected(true)}}
			onBlur={() => setSelected(false)}
		/>
		<Popper
			id={id}
			open={selected || hovered}
			anchorEl={anchorEl}
			sx={{zIndex: 99999, width: (anchorEl?.clientWidth ?? 480) * 0.985, overflow: 'auto', maxHeight: 200, border: 1, p: 1, bgcolor: 'background.paper'}}
			disablePortal
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			placement={'bottom-start'}
			>
				{searchingQuestions || searchingQuestionSets
					? <CircularProgress />
					: (questionSearchResults?.length || questionSetSearchResults?.length)
						? <List>
							<ListSubheader>
								<Typography variant="body1" fontWeight='700'>Question Groups</Typography>
							</ListSubheader>
							{questionSetSearchResults?.map((qs) => <>
								<ListItem alignItems='center'>
									<IconButton onClick={(e) => {props.onQuestionSetSelected ? props.onQuestionSetSelected(qs) : null}}>
										<Add />
									</IconButton>
									<Typography variant="body2">{qs.name}</Typography>
								</ListItem>
							</>) ?? <Typography variant="body1">No question groups found.</Typography>}

							<ListSubheader>
								<Typography variant="body1" fontWeight='700'>Questions</Typography>
							</ListSubheader>
							{questionSearchResults?.map((q) => <>
								<ListItem>
									<IconButton onClick={(e) => {props.onQuestionSelected ? props.onQuestionSelected(q) : null}}>
										<Add />
									</IconButton>
									<Typography variant="body2">{(q.latest_version ? q.versions[q.latest_version]: q.versions.find((v) => v.status == QuestionAuthoringStatus.DRAFT))?.text ?? <Error />}</Typography>
								</ListItem>
							</>) ?? <Typography variant="body1">No questions found.</Typography>}
						</List>
					: <Typography variant="h6">No results found for search text "{searchText}".</Typography>
				}
		</Popper>
	</>
}

export interface PublishChainModalProps {
	cache: QuestionnaireDataCache
	thisItem: string
	unpublishedVersions: UnpublishedChildren
	onPublish: () => void
	onClose: () => void
}

export const PublishChainModal = (props: PublishChainModalProps) => {
	return <Dialog open onClose={props.onClose} scroll='paper'>
		<DialogTitle>Publish</DialogTitle>
		<DialogContent>
			<Stack direction='column' spacing={2}>
				<Typography variant="h4">Publishing this entity will publish the following versions:</Typography>
				{props.unpublishedVersions.questionVersions && props.unpublishedVersions.questionVersions.length > 0 ? <>
					<Typography variant="h6">Questions:</Typography>
					{props.unpublishedVersions.questionVersions.map((x) => <QuestionVersionForm readOnly entityId={x} cache={props.cache} updateCache={() => {}} />)}
				</> : <></>}
				{props.unpublishedVersions.questionSetVersions && props.unpublishedVersions.questionSetVersions.length > 0 ? <>
					<Typography variant="h6">Question Groups:</Typography>
					{props.unpublishedVersions.questionSetVersions.map((x) => <>
						<Typography variant="subtitle1">{props.cache.questionSets[props.cache.questionSetVersions[x].question_set_id].name}</Typography>
						<QuestionSetVersionForm readOnly entityId={x} cache={props.cache} updateCache={() => {}} />
					</>)}
				</> : <></>}
				{props.unpublishedVersions.questionnaireVersions && props.unpublishedVersions.questionnaireVersions.length > 0 ? <>
					<Typography variant="h6">Questionnaires:</Typography>
					{props.unpublishedVersions.questionnaireVersions.map((x) => <>
						<Typography variant="subtitle1">{props.cache.questionnaires[props.cache.questionnaireVersions[x].questionnaire_id].name}</Typography>
						<QuestionnaireVersionForm readOnly entityId={x} cache={props.cache} updateCache={() => {}} />
					</>)}
				</> : <></>}

				<Typography variant="body1">Are you sure you would like to continue?</Typography>
			</Stack>
		</DialogContent>
		<DialogActions>
			<Button onClick={() => props.onClose()}>Cancel</Button>
			<Button onClick={() => props.onPublish()}>Publish</Button>
		</DialogActions>
	</Dialog>
}

const Transition = forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<unknown>;
	},
	ref: React.Ref<unknown>,
	) {
	return <Slide direction="up" ref={ref} {...props} />;
});

interface QuestionnaireFormSharedProps {
	entityId: string
	cache: QuestionnaireDataCache
	updateCache: (cache: QuestionnaireDataCache) => void
	root?: boolean
	readOnly?: boolean
}

export interface QuestionnaireSaveButtonProps {
	saveState: SaveState
	saveMessage: string
	onSave: () => void
	readOnly?: boolean
	draft?: boolean
	publish?: boolean
	newVersion?: boolean
}

export const QuestionnaireSaveButton = (props: QuestionnaireSaveButtonProps) => {
	let saveString = ''
	if (props.publish && props.newVersion) {
		saveString = "Publish new version"
	} else if (props.publish && props.draft) {
		saveString = "Publish draft"
	} else if (!props.publish && props.draft && !props.newVersion) {
		saveString = "Save draft"
	} else if (!props.publish && props.newVersion) {
		saveString = "Save new version as draft"
	} else {
		saveString = "Save"
	}

	return props.readOnly
		? <></>
		: (() => {
			switch (props.saveState) {
			case SaveState.UNMODIFIED:
				if ( ! props.publish) {
					return <Button variant='contained' disabled onClick={props.onSave}>{saveString} (no modifications)</Button>
				} else {
					return <Button variant='contained' onClick={props.onSave}>{saveString}</Button>
				}
			case SaveState.MODIFIED:
				return <Button variant='contained' onClick={props.onSave}>{saveString}</Button>
			case SaveState.SAVING:
				return <Button variant='contained' disabled endIcon={<CircularProgress />} onClick={props.onSave}>{saveString}</Button>
			case SaveState.SAVED_MODIFIED:
				return <Button variant='contained' endIcon={<Check />} onClick={props.onSave}>{saveString}</Button>
			case SaveState.SAVED:
				return <Button variant='contained' endIcon={<Check />} disabled onClick={props.onSave}>{saveString}</Button>
			case SaveState.SAVE_FAILED:
				return <Button variant='contained' endIcon={<Clear />} onClick={props.onSave}>{saveString} (failed: {props.saveMessage})</Button>
			}
		})();
}

export interface QuestionVersionFormProps extends QuestionnaireFormSharedProps {
	current?: boolean
}

export const QuestionVersionForm = (props: QuestionVersionFormProps) => {
	const questionVersion = props.cache.questionVersions[props.entityId]
	const isNew = questionVersion.id.startsWith("NEW")

	const onVersionChange = useCallback((v: QuestionVersion) => {
		const oq = props.cache.questions[v.question_id]
		props.updateCache({
			...props.cache,
			edits: new Set<string>([...props.cache.edits, MakeQuestionDataRef(v.id, QuestionDataType.QUESTION_VERSION)]),
			questions: {...props.cache.questions, [v.question_id]: {...oq, versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)}},
			questionVersions: {...props.cache.questionVersions, [v.id]: v}
		})
	}, [props.cache, props.updateCache])

	return <Stack direction='column' width='100%' spacing={2} key={questionVersion.id}>
		<Stack direction='row' spacing={2} width='100%'>
			{
				isNew
				? <Typography variant="subtitle2" color='gray'>New Question version</Typography>
				: (props.current
					? <Typography variant="subtitle2" color='gray'>Version {questionVersion.version} (current)</Typography>
					: (questionVersion.status == QuestionAuthoringStatus.DRAFT
						? <Typography variant="subtitle2" color='gray'>Current Draft</Typography>
						: <Typography variant="subtitle2" color='gray'>Version {questionVersion.version}</Typography>
					)
				)
			}
			<Typography variant="body2" color="gray" justifySelf={'right'} marginLeft={'100%'}>{questionVersion.id}</Typography>
		</Stack>
		<TextField value={questionVersion.comment} disabled={props.readOnly} onChange={(e) => onVersionChange({...questionVersion, comment: e.target.value})} label={<Typography variant='body1'>Comment</Typography>} fullWidth></TextField>
		<TextField value={questionVersion.text} disabled={props.readOnly} onChange={(e) => onVersionChange({...questionVersion, text: e.target.value})} label={<Typography variant='body1'>Question</Typography>} fullWidth></TextField>
		<Stack direction='row' width={'100%'} spacing={1}>
			<FormControl fullWidth>
				<InputLabel id={`${questionVersion.id}-qt-label`}>Question type</InputLabel>
				<Select fullWidth readOnly={props.readOnly} disabled={props.readOnly} value={questionVersion.question_type} onChange={(e) => onVersionChange({...questionVersion, question_type: e.target.value})} id={`${questionVersion.id}-qt`} labelId={`${questionVersion.id}-qt-label`} label='Question type'>
					<MenuItem value={QuestionType.YES_NO_PARTIAL}>
						{QuestionType.YES_NO_PARTIAL}
					</MenuItem>
				</Select>
			</FormControl>
			<FormControl fullWidth>
				<InputLabel id={`${questionVersion.id}-ev-label`}>Evidence requirement</InputLabel>
				<Select fullWidth readOnly={props.readOnly} disabled={props.readOnly} value={questionVersion.question_evidence_requirement} onChange={(e) => onVersionChange({...questionVersion, question_evidence_requirement: e.target.value})} id={`${questionVersion.id}-ev`} labelId={`${questionVersion.id}-ev-label`} label='Evidence requirement'>
					<MenuItem value={QuestionEvidenceRequirement.ALWAYS}>
						{QuestionEvidenceRequirement.ALWAYS}
					</MenuItem>
					<MenuItem value={QuestionEvidenceRequirement.SOMETIMES}>
						{QuestionEvidenceRequirement.SOMETIMES}
					</MenuItem>
					<MenuItem value={QuestionEvidenceRequirement.NOT_APPLICABLE}>
						{QuestionEvidenceRequirement.NOT_APPLICABLE}
					</MenuItem>
					<MenuItem value={QuestionEvidenceRequirement.NEVER}>
						{QuestionEvidenceRequirement.NEVER}
					</MenuItem>
				</Select>
			</FormControl>
		</Stack>
	</Stack>
}

export interface QuestionFormProps extends QuestionnaireFormSharedProps {
	singleVersion?: string
}

export const QuestionForm = (props: QuestionFormProps) => {
	const question = props.cache.questions[props.entityId]

	const [saveState, setSaveState] = useState<SaveState>(SaveState.UNMODIFIED)
	const [saveMessage, setSaveMessage] = useState<string>('')

	const orgId = useContext(OrganizationContext)

	if (orgId == null) {
		return <Typography variant="h1">Invalid organization, try logging out and back in?</Typography>
	}

	const saveDraft = useCallback(() => {
		saveQuestionnaireDataToBackend(orgId, props.cache, saveState, setSaveState, setSaveMessage, (newCache) => {
			props.updateCache(newCache)
		})
	}, [orgId, saveState, setSaveState, setSaveMessage, props.cache, props.updateCache])

	const publish = useCallback((versionId: string) => {
		saveQuestionnaireDataToBackend(orgId, props.cache, saveState, setSaveState, setSaveMessage, (newCache) => {
			const permanentId = versionId.startsWith("NEW:") ? newCache.idMap[versionId] : versionId
			new Promise<QuestionVersion>((resolve, reject) => publishQuestionVersion({question: newCache.questionVersions[permanentId].question_id, version: permanentId}, undefined, (data, resp, err) => {
				if (resp?.status != 200) {
					reject(resp?.statusText)
				} else if (err) {
					reject(err.toJSON())
				} else if (data) {
					resolve(data)
				}
			})).then((version) => {
				props.updateCache({
					...newCache,
					questionVersions: {
						...newCache.questionVersions,
						[version.id]: version
					},
					questions: {
						...newCache.questions,
						[version.question_id]: {
							...newCache.questions[version.question_id],
							latest_version: version.id,
							versions: newCache.questions[version.question_id].versions.map((x) => x.id == version.id ? version : x)
						}
					}
				})
			})
		})
	}, [orgId, saveState, setSaveState, setSaveMessage, props.cache, props.updateCache])

	const updateCache = useCallback((cache: QuestionnaireDataCache) => {
		setSaveState(SaveState.MODIFIED)
		props.updateCache(cache)
	}, [setSaveState, props.updateCache, props.cache]);

	const createDraft = useCallback((questionId: string) => {
		let ver = instantiateQuestionVersion(questionId)
		const latestVersionId = props.cache.questions[questionId].latest_version
		if (latestVersionId) {
			const latestVersion = props.cache.questionVersions[latestVersionId]
			ver.text = latestVersion.text
			ver.question_type = latestVersion.question_type
			ver.question_evidence_requirement = latestVersion.question_evidence_requirement
		}

		updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(props.entityId, QuestionDataType.QUESTION)]),
			questionVersions: {
				...props.cache.questionVersions,
				[ver.id]: ver
			},
			questions: {
				...props.cache.questions,
				[questionId]: {
					...props.cache.questions[questionId],
					versions: props.cache.questions[questionId].versions.concat([ver])
				}
			}
		})
	}, [updateCache, props.cache])

	const readOnly = props.readOnly || saveState == SaveState.SAVING;
	const newQuestion = question.id.startsWith("NEW")
	const draftVersion = question.versions.filter((x) => x.status == QuestionAuthoringStatus.DRAFT).at(0)
	const hasDraft = !!draftVersion

	return <Stack direction='column' width='100%' key={question.id} spacing={2}>
		<Stack direction='row' spacing={2} width='100%' alignItems='center'>
			<Typography variant="subtitle1">Question</Typography>
			{
				question.id.startsWith("NEW") ? <Chip label='New' /> : <></>
			}
			<Typography variant="body2" color="gray" justifySelf={'right'} marginLeft={'100%'}>{question.id}</Typography>
		</Stack>
		<Paper sx={{p:2}}>
			<Stack direction='column'>
				{question.versions.filter((x) => !props.singleVersion || x.id == props.singleVersion ).toSorted((a, b) => a.status == QuestionAuthoringStatus.DRAFT ? -1 : (b.version ?? 0) - (a.version ?? 0)).map((version, index) => {return <div key={index}>
					<Accordion key={`${version.id}-${index}`} defaultExpanded={version.status == QuestionAuthoringStatus.DRAFT} elevation={2}>
						<AccordionSummary expandIcon={<ExpandMore />}>
							<Stack direction='row' alignItems={'center'} spacing={2}>
								{(version.status == QuestionAuthoringStatus.DRAFT)
									? <Typography variant='subtitle1' component="span" color="gray">Draft: {version.comment} ({version.text})</Typography>
									: <Typography variant='subtitle1' component="span" color="gray">Version {version.version}: {version.comment} ({version.text})</Typography>
								}
								{version.status == QuestionAuthoringStatus.PUBLISHED && question.latest_version == version.id
									? <Chip label="Latest version" />
									: <></>
								}
							</Stack>
						</AccordionSummary>
						<Divider orientation='horizontal' flexItem />
						<AccordionDetails>
							<QuestionVersionForm readOnly={readOnly || version.status != QuestionAuthoringStatus.DRAFT} entityId={version.id} cache={props.cache} updateCache={updateCache} />
						</AccordionDetails>
					</Accordion>
				</div>})}
			</Stack>
		</Paper>
		{props.root && hasDraft ? <QuestionnaireSaveButton onSave={saveDraft} saveMessage={saveMessage} saveState={saveState} draft newVersion={newQuestion} /> : <></>}
		{props.root && hasDraft ? <QuestionnaireSaveButton onSave={() => publish(draftVersion.id)} saveMessage={saveMessage} saveState={saveState} publish draft newVersion={newQuestion} /> : <></>}
		{props.root && !hasDraft ? <Button variant='contained' endIcon={<Drafts />} onClick={() => createDraft(question.id)}>Create Draft</Button> : <></>}
	</Stack>
}

export interface QuestionSetVersionEntryFormProps extends QuestionnaireFormSharedProps {
	data: QuestionSetEntry
}

export const QuestionSetVersionEntryForm = (props: QuestionSetVersionEntryFormProps) => {
	const {data} = props

	const updateCache = useCallback((cache: QuestionnaireDataCache) => {
		const ov = cache.questionSetVersions[data.question_set_id]
		const oq = cache.questionSets[ov.question_set_id]
		const nv: QuestionSetVersion = {
			...ov, entries: ov.entries.map((oe) => {
				return {
					...oe,
					question: cache.questionVersions[oe.question.question_id],
				}
			})
		}
		props.updateCache({
			...cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.question_set_id, QuestionDataType.QUESTION_SET_VERSION)]),
			questionSets: {
				...cache.questionSets,
				[oq.id]: {
					...oq,
					versions: oq.versions.map((oov) => oov.id == nv.id ? nv : oov),
				}
			}
		})
	}, [props.updateCache, data]);

	return <Box key={data.id}>
		<Paper sx={{p:1}}>
			<Stack direction='row'>
				{(data.question && props.cache.questions[data.question.question_id]
					? <QuestionForm readOnly={props.readOnly} entityId={data.question.question_id} singleVersion={data.question.id} cache={props.cache} updateCache={updateCache} />
					: <Typography variant="body1">Error: Question not found</Typography>
				)}
			</Stack>
		</Paper>
	</Box>
}

export interface QuestionSetVersionFormProps extends QuestionnaireFormSharedProps {
}

export const QuestionSetVersionForm = (props: QuestionSetVersionFormProps) => {
	const data = props.cache.questionSetVersions[props.entityId]

	const onVersionChange = useCallback((v: QuestionSetVersion) => {
		const oq = props.cache.questionSets[data.question_set_id]

		props.updateCache({...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTION_SET_VERSION)]),
			questionSets: {...props.cache.questionSets, [v.question_set_id]: {...oq, versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)}},
			questionSetVersions: {...props.cache.questionSetVersions, [v.id]: v}
		})
	}, [props.cache, props.updateCache, data])

	const onCreateQuestion = useCallback(() => {
		let question = instantiateQuestion()
		let entry = instantiateQuestionSetVersionEntry(data.id, question.versions[0])
		const oq = props.cache.questionSets[data.question_set_id]
		const v = {...data, entries: [...data.entries, entry]}

		props.updateCache({...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTION_SET_VERSION)]),
			questions: {
				...props.cache.questions,
				[question.id]: question
			}, questionVersions: {
				...props.cache.questionVersions,
				[question.versions[0].id]: question.versions[0]
			}, questionSets: {
				...props.cache.questionSets, [v.question_set_id]: {...oq, versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)}
			}, questionSetVersions: {...props.cache.questionSetVersions, [v.id]: v}
		})
	}, [data, props.cache, props.updateCache]);


	const onAddQuestion = useCallback((q: Question) => {
		let newCache = {...props.cache,
			questions: {...props.cache.questions, [q.id]: q},
			questionVersions: q.versions.reduce((acc, v) => {return {...acc, [v.id]: v}}, props.cache.questionVersions)
		}
		let missing = getQuestionChildrenMissingFromCache(newCache, q)
		retrieveQuestionData(missing, newCache, (newerCache) => {
			const latestDraft = q.versions.find((x) => x.status == QuestionAuthoringStatus.DRAFT)
			const version = q.latest_version ?? latestDraft?.id
			if ( ! version) {
				return
			}
			let entry = instantiateQuestionSetVersionEntry(data.id, newerCache.questionVersions[version])
			const oq = newerCache.questionSets[data.question_set_id]
			const v = {...data, entries: [...data.entries, entry]}

			props.updateCache({...newerCache,
				edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTION_SET_VERSION)]),
				questionSets: {
					...newerCache.questionSets, [v.question_set_id]: {...oq, versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)}
				},
				questionSetVersions: {...newerCache.questionSetVersions, [v.id]: v}
			})
		})
	}, [data, props.cache, props.updateCache]);

	return <Box key={data.id}>
		<Stack direction="column" spacing={2} paddingTop={1}>
			<Stack direction='row' spacing={2} width='100%' alignItems='center'>
				<Typography variant="subtitle1">Question Group version</Typography>
				{
					data.id.startsWith("NEW") ? <Chip label='New' /> : <></>
				}
				<Typography variant="body2" color="gray" justifySelf={'right'} marginLeft={'100%'}>{data.id}</Typography>
			</Stack>
			<TextField label={<Typography variant="body1">Comment</Typography>} disabled={props.readOnly} onChange={(e) => onVersionChange({...data, comment: e.target.value})} value={data.comment}></TextField>
			<Paper sx={{p:1}}>
				<Stack direction='column' spacing={1}>
					<Typography variant='subtitle1'>Questions</Typography>

					{data.entries.map((entry, index) => {return <div key={index}>
						<QuestionSetVersionEntryForm readOnly={props.readOnly} entityId={entry.id} data={entry} cache={props.cache} updateCache={props.updateCache} />
					</div>})}

					{!props.readOnly
						? <Stack direction='column' spacing={1}>
							<Typography variant="subtitle1" color="gray">Add a question to this group:</Typography>
							<Stack direction='row' sx={{p: 1}}>
								<QuestionSearch cache={props.cache} selectedIds={data.entries.map((x) => x.question.question_id)} searchQuestions onQuestionSelected={(q) => {
									onAddQuestion(q)
								}} />
							</Stack>
							<Stack direction='row'>
								<Button fullWidth startIcon={<Create />} onClick={onCreateQuestion}>Create new question</Button>
							</Stack>
						</Stack>
						: <></>
					}
				</Stack>
			</Paper>
		</Stack>
	</Box>
}

export interface QuestionSetFormProps extends QuestionnaireFormSharedProps {
	singleVersion?: string
}

export const QuestionSetForm = (props: QuestionSetFormProps) => {
	const data = props.cache.questionSets[props.entityId]

	const [saveState, setSaveState] = useState<SaveState>(SaveState.UNMODIFIED)
	const [saveMessage, setSaveMessage] = useState<string>('')

	const orgId = useContext(OrganizationContext)

	if (orgId == null) {
		return <Typography variant="h1">Invalid organization, try logging out and back in?</Typography>
	}

	const saveDraft = useCallback(() => {
		saveQuestionnaireDataToBackend(orgId, props.cache, saveState, setSaveState, setSaveMessage, (newCache) => {
			props.updateCache(newCache)
		})
	}, [orgId, saveState, setSaveState, setSaveMessage, props.cache, props.updateCache])

	const publish = useCallback((unpublished: UnpublishedChildren) => {
		saveQuestionnaireDataToBackend(orgId, props.cache, saveState, setSaveState, setSaveMessage, (newCache) => {
			const unpublishedNewIds: UnpublishedChildren = {
				questionVersions: unpublished.questionVersions?.map((x) => newCache.idMap[x] ?? x),
				questionSetVersions: unpublished.questionSetVersions?.map((x) => newCache.idMap[x] ?? x),
				questionnaireVersions: unpublished.questionnaireVersions?.map((x) => newCache.idMap[x] ?? x),
			}
			publishAll(newCache, unpublishedNewIds, (newerCache) => {
				props.updateCache(newerCache)
			})
		})
	}, [orgId, saveState, setSaveState, setSaveMessage, props.cache, props.updateCache])

	const updateCache = useCallback((cache: QuestionnaireDataCache) => {
		setSaveState(SaveState.MODIFIED)
		props.updateCache(cache)
	}, [setSaveState, props.updateCache]);

	const onChange = useCallback((q: QuestionSet) => {
		updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(props.entityId, QuestionDataType.QUESTION_SET)]),
			questionSets: {...props.cache.questionSets, [q.id]: q},
		})
	}, [updateCache, props.cache, props.entityId])

	const createDraft = useCallback((questionSetId: string) => {
		let ver = instantiateQuestionSetVersion(questionSetId)
		const latestVersionId = props.cache.questionSets[questionSetId].latest_version
		if (latestVersionId) {
			const latestVersion = props.cache.questionSetVersions[latestVersionId]
			ver.entries = latestVersion.entries.map((e) => {return {...e, id: generateNewLocalId()}})
		}

		updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(props.entityId, QuestionDataType.QUESTION_SET)]),
			questionSetVersions: {
				...props.cache.questionSetVersions,
				[ver.id]: ver
			},
			questionSets: {
				...props.cache.questionSets,
				[questionSetId]: {
					...props.cache.questionSets[questionSetId],
					versions: props.cache.questionSets[questionSetId].versions.concat([ver])
				}
			}
		})
	}, [updateCache, props.cache])

	const readOnly = props.readOnly || saveState == SaveState.SAVING;
	const newQuestionSet = data.id.startsWith("NEW")
	const draftVersion = data.versions.filter((x) => x.status == QuestionAuthoringStatus.DRAFT).at(0)
	const hasDraft = !!draftVersion

	const [showPublishDraftModal, setShowPublishDraftModal] = useState(false)
	const [unpublishedChildren, setUnpublishedChildren] = useState<UnpublishedChildren | undefined>(undefined)

	const onPublish = useCallback(() => {
		if (draftVersion) {
			setUnpublishedChildren(getQuestionSetVersionUnpublishedChildren(props.cache, draftVersion.id))
			setShowPublishDraftModal(true)
		}
	}, [publish, props.updateCache, props.cache, setShowPublishDraftModal, setUnpublishedChildren, draftVersion])

	return <Stack direction='column' width='100%' key={data.id} spacing={2} paddingTop={1}>
		<Stack direction='row' spacing={2} width='100%' alignItems='center'>
			<Typography variant="subtitle1">Question Group</Typography>
			{
				data.id.startsWith("NEW") ? <Chip label='New' /> : <></>
			}
			<Typography variant="body2" color="gray" justifySelf={'right'} marginLeft={'100%'}>{data.id}</Typography>
		</Stack>
		<TextField label={<Typography variant="body1">Name</Typography>} disabled={readOnly} value={data.name} onChange={(e) => onChange({...data, name: e.target.value})}></TextField>

		<Paper sx={{p:1}}>
			<Stack direction='column'>
				{data.versions.filter((x) => !props.singleVersion || x.id == props.singleVersion).toSorted((a, b) => a.status == QuestionAuthoringStatus.DRAFT ? -1 : (b.version ?? 0) - (a.version ?? 0)).map((version, index) => {return <div key={index}>
					<Accordion defaultExpanded={version.status == QuestionAuthoringStatus.DRAFT} elevation={2}>
						<AccordionSummary expandIcon={<ExpandMore />}>
							<Stack direction='row' alignItems='center' spacing={2}>
								{(version.status == QuestionAuthoringStatus.DRAFT)
									? <Typography variant='subtitle1' component="span" color="gray">Draft: {version.comment}</Typography>
									: <Typography variant='subtitle1' component="span" color="gray">Version {version.version}: {version.comment}</Typography>
								}
								{version.status == QuestionAuthoringStatus.PUBLISHED && data.latest_version == version.id
									? <Chip label="Latest version" />
									: <></>
								}
							</Stack>
						</AccordionSummary>
						<Divider orientation='horizontal' flexItem />
						<AccordionDetails>
							<QuestionSetVersionForm readOnly={readOnly || version.status != QuestionAuthoringStatus.DRAFT} entityId={version.id} cache={props.cache} updateCache={updateCache}/>
						</AccordionDetails>
					</Accordion>
				</div>})}
			</Stack>
		</Paper>

		{props.root && hasDraft ? <QuestionnaireSaveButton onSave={saveDraft} saveMessage={saveMessage} saveState={saveState} draft newVersion={newQuestionSet} /> : <></>}
		{props.root && hasDraft ? <QuestionnaireSaveButton onSave={onPublish} saveMessage={saveMessage} saveState={saveState} publish draft newVersion={newQuestionSet} /> : <></>}
		{props.root && !hasDraft ? <Button variant='contained' endIcon={<Drafts />} onClick={() => createDraft(props.entityId)}>Create Draft</Button> : <></>}
		{props.root && !hasDraft ? <QuestionnaireSaveButton onSave={saveDraft} saveMessage={saveMessage} saveState={saveState} /> : <></>}

		{showPublishDraftModal && unpublishedChildren && draftVersion
			? <PublishChainModal
				unpublishedVersions={unpublishedChildren} thisItem={draftVersion.id} cache={props.cache}
				onClose={() => setShowPublishDraftModal(false)} onPublish={() => publish(unpublishedChildren)} />
			: <></>}
	</Stack>
}

export interface QuestionnaireEntryFormProps extends QuestionnaireFormSharedProps {
	data: QuestionnaireEntry
}

export const QuestionnaireEntryForm = (props: QuestionnaireEntryFormProps) => {
	const {data} = props

	const updateCache = useCallback((cache: QuestionnaireDataCache) => {
		const ov = cache.questionnaireVersions[data.questionnaire_id]
		const oq = cache.questionnaires[ov.questionnaire_id]
		const nv: QuestionnaireVersion = {
			...ov, entries: ov.entries.map((oe) => {
				return {
					...oe,
					question: oe.question ? cache.questionVersions[oe.question.question_id] : undefined,
					question_set: oe.question_set ? cache.questionSetVersions[oe.question_set.question_set_id] : undefined,
				}
			})
		}
		props.updateCache({
			...cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.questionnaire_id, QuestionDataType.QUESTIONNAIRE_VERSION)]),
			questionnaires: {
				...cache.questionnaires,
				[oq.id]: {
					...oq,
					versions: oq.versions.map((oov) => oov.id == nv.id ? nv : oov),
				}
			}
		})
	}, [props.updateCache, data.questionnaire_id]);

	return <Box key={data.id}>
		<Paper sx={{p:1}}>
			<Stack direction='row'>
				{data.entry_type == QuestionnaireEntryType.QUESTION
					? (data.question
							? <QuestionForm entityId={data.question.question_id} singleVersion={data.question.id} cache={props.cache} updateCache={updateCache} readOnly={props.readOnly} />
							: <Typography variant="body1">Error: Question not found</Typography>
					) : (data.entry_type == QuestionnaireEntryType.QUESTION_SET
						? (data.question_set
							? <QuestionSetForm entityId={data.question_set.question_set_id} cache={props.cache} updateCache={updateCache} readOnly={props.readOnly} />
							: <Typography variant="body1">Error: Question Group not found</Typography>
						)
						: <Typography variant="body1">Error: Invalid questionnaire entry</Typography>
					)
				}
			</Stack>
		</Paper>
	</Box>
}

export interface QuestionnaireVersionFormProps extends QuestionnaireFormSharedProps {
}

export const QuestionnaireVersionForm = (props: QuestionnaireVersionFormProps) => {
	const data = props.cache.questionnaireVersions[props.entityId]

	const onChange = useCallback((v: QuestionnaireVersion) => {
		const oq = props.cache.questionnaires[v.questionnaire_id]
		props.updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(v.id, QuestionDataType.QUESTIONNAIRE_VERSION)]),
			questionnaireVersions: {
				...props.cache.questionnaireVersions,
				[v.id]: v
			}, questionnaires: {
				...props.cache.questionnaires,
				[oq.id]: {
					...oq,
					versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)
				}
			}
		})
	}, [props.updateCache, props.cache])

	const onCreateQuestion = useCallback(() => {
		const question = instantiateQuestion()
		let entry = instantiateQuestionnaireEntry(data.id, QuestionnaireEntryType.QUESTION)
		entry.question = question.versions[0]
		const v = {...data, entries: [...data.entries, entry]}
		const oq = props.cache.questionnaires[v.questionnaire_id]

		props.updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTIONNAIRE_VERSION)]),
			questions: {
					...props.cache.questions,
					[question.id]: question
			}, questionVersions: {
				...props.cache.questionVersions,
				[question.versions[0].id]: question.versions[0]
			}, questionnaireVersions: {
					...props.cache.questionnaireVersions,
					[v.id]: v
			}, questionnaires: {
				...props.cache.questionnaires,
				[oq.id]: {
					...oq,
					versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)
				}
			}
		})
	}, [data, props.cache, props.updateCache]);

	const onAddQuestion = useCallback((q: Question) => {
		let newCache = {...props.cache,
			questions: {...props.cache.questions, [q.id]: q},
			questionVersions: q.versions.reduce((acc, v) => {return {...acc, [v.id]: v}}, props.cache.questionVersions)
		}
		let missing = getQuestionChildrenMissingFromCache(newCache, q)
		retrieveQuestionData(missing, newCache, (newerCache) => {
			const latestDraft = q.versions.find((x) => x.status == QuestionAuthoringStatus.DRAFT)
			const version = q.latest_version ?? latestDraft?.id
			if ( ! version) {
				return
			}
			let entry = instantiateQuestionnaireEntry(data.id, QuestionnaireEntryType.QUESTION)
			entry.question = newerCache.questionVersions[version]
			const oq = newerCache.questionnaires[data.questionnaire_id]
			const v = {...data, entries: [...data.entries, entry]}

			props.updateCache({...newerCache,
				edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTIONNAIRE_VERSION)]),
				questionnaires: {
					...newerCache.questionnaires, [v.questionnaire_id]: {...oq, versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)}
				},
				questionnaireVersions: {...newerCache.questionnaireVersions, [v.id]: v}
			})
		})
	}, [data, props.cache, props.updateCache]);

	const onCreateQuestionSet = useCallback(() => {
		const questionSet = instantiateQuestionSet()
		let entry = instantiateQuestionnaireEntry(data.id, QuestionnaireEntryType.QUESTION_SET)
		entry.question_set = questionSet.versions[0]
		const v = {...data, entries: [...data.entries, entry]}
		const oq = props.cache.questionnaires[v.questionnaire_id]

		props.updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTIONNAIRE_VERSION)]),
			questionSets: {
				...props.cache.questionSets,
				[questionSet.id]: questionSet
			}, questionSetVersions: {
				...props.cache.questionSetVersions,
				[questionSet.versions[0].id]: questionSet.versions[0]
			}, questionnaireVersions: {
					...props.cache.questionnaireVersions,
					[v.id]: v
			}, questionnaires: {
				...props.cache.questionnaires,
				[oq.id]: {
					...oq,
					versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)
				}
			}
		})
	}, [data, props.cache, props.updateCache]);

	const onAddQuestionSet = useCallback((q: QuestionSet) => {
		let newCache = {...props.cache,
			questionSets: {...props.cache.questionSets, [q.id]: q},
			questionSetVersions: q.versions.reduce((acc, v) => {return {...acc, [v.id]: v}}, props.cache.questionSetVersions)
		}
		let missing = getQuestionSetChildrenMissingFromCache(newCache, q)
		retrieveQuestionData(missing, newCache, (newerCache) => {
			const latestDraft = q.versions.find((x) => x.status == QuestionAuthoringStatus.DRAFT)
			const version = q.latest_version ?? latestDraft?.id
			if ( ! version) {
				return
			}
			let entry = instantiateQuestionnaireEntry(data.id, QuestionnaireEntryType.QUESTION_SET)
			entry.question_set = newerCache.questionSetVersions[version]
			const oq = newerCache.questionnaires[data.questionnaire_id]
			const v = {...data, entries: [...data.entries, entry]}

			props.updateCache({...newerCache,
				edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(data.id, QuestionDataType.QUESTIONNAIRE_VERSION)]),
				questionnaires: {
					...newerCache.questionnaires, [v.questionnaire_id]: {...oq, versions: oq.versions.map((ov) => ov.id == v.id ? v : ov)}
				},
				questionnaireVersions: {...newerCache.questionnaireVersions, [v.id]: v}
			})
		})
	}, [data, props.cache, props.updateCache]);

	return <Box key={data.id}>
		<Stack direction="column" spacing={2} paddingTop={1}>
			<Stack direction='row' spacing={2} width='100%'>
				{
					data.id.startsWith("NEW") ? <Typography variant="subtitle2" color='gray'>New Questionnaire version</Typography> : <Typography variant="subtitle2" color='gray'>Draft</Typography>
				}
				<Typography variant="body2" color="gray" justifySelf={'right'} marginLeft={'100%'}>{data.id}</Typography>
			</Stack>
			<TextField label={<Typography variant="body1">Comment</Typography>} disabled={props.readOnly} onChange={(e) => onChange({...data, comment: e.target.value})} value={data.comment}></TextField>
			<Paper sx={{p:1}}>
				<Stack direction='column' spacing={1}>
					<Typography variant='subtitle1'>Questions</Typography>

					{data.entries.map((entry, index) => {return <div key={index}>
						<QuestionnaireEntryForm entityId={entry.id} data={entry} cache={props.cache} updateCache={props.updateCache} readOnly={props.readOnly} />
					</div>})}

					{!props.readOnly
						? <Stack direction='column' spacing={1}>
							<Typography variant="subtitle1" color="gray">Add a question or group:</Typography>
							<Stack direction='row' sx={{p: 1}}>
								<QuestionSearch cache={props.cache} selectedIds={data.entries.map((x) => x.question?.question_id ?? x.question_set?.question_set_id ?? '')} searchQuestions searchQuestionSets
									onQuestionSelected={(q) => onAddQuestion(q)}
									onQuestionSetSelected={(qs) => onAddQuestionSet(qs)}
								/>
							</Stack>
							<Stack direction='row'>
								<Button fullWidth startIcon={<Create />} onClick={onCreateQuestion}>Create new question</Button>
								<Button fullWidth startIcon={<CreateNewFolder />} onClick={onCreateQuestionSet}>Create new group</Button>
							</Stack>
						</Stack>
						: <></>
					}
				</Stack>
			</Paper>
		</Stack>
	</Box>
}

export interface QuestionnaireFormProps extends QuestionnaireFormSharedProps {
	singleVersion?: string
}

export const QuestionnaireForm = (props: QuestionnaireFormProps) => {
	const questionnaire = props.cache.questionnaires[props.entityId]

	const orgId = useContext(OrganizationContext)

	const [saveState, setSaveState] = useState<SaveState>(SaveState.UNMODIFIED)
	const [saveMessage, setSaveMessage] = useState<string>('')

	if (orgId == null) {
		return <Typography variant="h1">Invalid organization, try logging out and back in?</Typography>
	}

	const saveDraft = useCallback(() => {
		saveQuestionnaireDataToBackend(orgId, props.cache, saveState, setSaveState, setSaveMessage, (newCache) => {
			props.updateCache(newCache)
		})
	}, [saveState, setSaveState, setSaveMessage, props.cache, orgId, props.updateCache])

	const publish = useCallback((unpublished: UnpublishedChildren) => {
		saveQuestionnaireDataToBackend(orgId, props.cache, saveState, setSaveState, setSaveMessage, (newCache) => {
			const unpublishedNewIds: UnpublishedChildren = {
				questionVersions: unpublished.questionVersions?.map((x) => newCache.idMap[x] ?? x),
				questionSetVersions: unpublished.questionSetVersions?.map((x) => newCache.idMap[x] ?? x),
				questionnaireVersions: unpublished.questionnaireVersions?.map((x) => newCache.idMap[x] ?? x),
			}
			publishAll(newCache, unpublishedNewIds, (newerCache) => {
				props.updateCache(newerCache)
			})
		})
	}, [orgId, saveState, setSaveState, setSaveMessage, props.cache, props.updateCache])

	const updateCache = useCallback((cache: QuestionnaireDataCache) => {
		setSaveState(SaveState.MODIFIED)
		props.updateCache(cache)
	}, [setSaveState, props.updateCache]);

	const onChange = useCallback((q: Questionnaire) => {
		updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(q.id, QuestionDataType.QUESTIONNAIRE)]),
			questionnaires: {
				...props.cache.questionnaires,
				[q.id]: q,
			}
		})
	}, [props.cache, updateCache])

	const createDraft = useCallback((questionnaireId: string) => {
		let ver = instantiateQuestionnaireVersion(questionnaireId)
		const latestVersionId = props.cache.questionnaires[questionnaireId].latest_version
		if (latestVersionId) {
			const latestVersion = props.cache.questionnaireVersions[latestVersionId]
			ver.entries = latestVersion.entries.map((e) => {return {...e, id: generateNewLocalId()}})
		}

		updateCache({
			...props.cache,
			edits: new Set<string>([...Array.from(props.cache.edits), MakeQuestionDataRef(props.entityId, QuestionDataType.QUESTIONNAIRE)]),
			questionnaireVersions: {
				...props.cache.questionnaireVersions,
				[ver.id]: ver
			},
			questionnaires: {
				...props.cache.questionnaires,
				[questionnaireId]: {
					...props.cache.questionnaires[questionnaireId],
					versions: props.cache.questionnaires[questionnaireId].versions.concat([ver])
				}
			}
		})
	}, [updateCache, props.cache])

	const readOnly = props.readOnly || saveState == SaveState.SAVING;
	const newQuestionnaire = questionnaire.id.startsWith("NEW")
	const draftVersion = questionnaire.versions.filter((x) => x.status == QuestionAuthoringStatus.DRAFT).at(0)
	const hasDraft = !!draftVersion

	const [showPublishDraftModal, setShowPublishDraftModal] = useState(false)
	const [unpublishedChildren, setUnpublishedChildren] = useState<UnpublishedChildren | undefined>(undefined)

	const onPublish = useCallback(() => {
		if (draftVersion) {
			setUnpublishedChildren(getQuestionnaireVersionUnpublishedChildren(props.cache, draftVersion.id))
			setShowPublishDraftModal(true)
		}
	}, [publish, props.updateCache, props.cache, setShowPublishDraftModal, setUnpublishedChildren, draftVersion])

	return <Box component="form">
		<Stack direction="column" spacing={2} paddingTop={1}>
			<Stack direction='row' spacing={2} width='100%'>
				{
					questionnaire.id.startsWith("NEW") ? <Typography variant="subtitle2" color='gray'>New Questionnaire</Typography> : <Typography variant="subtitle2" color='gray'>Draft</Typography>
				}
				<Typography variant="body2" color="gray" justifySelf={'right'} marginLeft={'100%'}>{questionnaire.id}</Typography>
			</Stack>
			<TextField label={<Typography variant="body1">Questionnaire name</Typography>} disabled={readOnly} onChange={(e) => onChange({...questionnaire, name: e.target.value})} value={questionnaire.name}></TextField>
			<Paper sx={{p:1}}>
				{questionnaire.versions.filter((x) => !props.singleVersion || x.id == props.singleVersion).toSorted((a, b) => a.status == QuestionAuthoringStatus.DRAFT ? -1 : (b.version ?? 0) - (a.version ?? 0)).map((version, index) => {return <div key={index}>
					<Accordion defaultExpanded={version.status == QuestionAuthoringStatus.DRAFT}>
						<AccordionSummary expandIcon={<ExpandMore />}>
							<Stack direction='row' alignItems='center' spacing={2}>
								{(version.status == QuestionAuthoringStatus.DRAFT)
									? <Typography variant='subtitle1' component="span" color="gray">Draft: {version.comment}</Typography>
									: <Typography variant='subtitle1' component="span" color="gray">Version {version.version}: {version.comment}</Typography>
								}
								{version.status == QuestionAuthoringStatus.PUBLISHED && questionnaire.latest_version == version.id
									? <Chip label="Latest version" />
									: <></>
								}
							</Stack>
						</AccordionSummary>
						<Divider orientation='horizontal' flexItem />
						<AccordionDetails>
							<QuestionnaireVersionForm readOnly={readOnly || version.status != QuestionAuthoringStatus.DRAFT} entityId={version.id} cache={props.cache} updateCache={updateCache}/>
						</AccordionDetails>
					</Accordion>
				</div>})}
			</Paper>
			{props.root && hasDraft ? <QuestionnaireSaveButton onSave={saveDraft} saveMessage={saveMessage} saveState={saveState} draft newVersion={newQuestionnaire} /> : <></>}
			{props.root && hasDraft ? <QuestionnaireSaveButton onSave={onPublish} saveMessage={saveMessage} saveState={saveState} publish draft newVersion={newQuestionnaire} /> : <></>}
			{props.root && !hasDraft ? <Button variant='contained' endIcon={<Drafts />} onClick={() => createDraft(props.entityId)}>Create Draft</Button> : <></>}
			{props.root && !hasDraft ? <QuestionnaireSaveButton onSave={saveDraft} saveMessage={saveMessage} saveState={saveState} /> : <></>}

			{showPublishDraftModal && unpublishedChildren && draftVersion
				? <PublishChainModal
					unpublishedVersions={unpublishedChildren} thisItem={draftVersion.id} cache={props.cache}
					onClose={() => setShowPublishDraftModal(false)} onPublish={() => publish(unpublishedChildren)} />
				: <></>}
		</Stack>
	</Box>
}

export interface CreateQuestionnaireModalProps {
	onClose: () => void
}

export const CreateQuestionnaireModal = (props: CreateQuestionnaireModalProps) => {
	const [questionnaire, setQuestionnaire] = useState<Questionnaire>({...instantiateQuestionnaire()})
	const [cache, updateCache] = useState<QuestionnaireDataCache>({
		...instantiateQuestionnaireCache(),
		questionnaires: {[questionnaire.id]: questionnaire}, questionnaireVersions: {[questionnaire.versions[0].id]: questionnaire.versions[0]}
	})

	const updateCacheAndQuestionnaire = useCallback((newCache: QuestionnaireDataCache) => {
		updateCache(newCache)
		if (newCache.idMap[questionnaire.id] && questionnaire.id.startsWith("NEW")) {
			setQuestionnaire(newCache.questionnaires[newCache.idMap[questionnaire.id]])
		}
	}, [questionnaire, updateCache, setQuestionnaire])

	return <Dialog open fullScreen slots={{transition: Transition}}>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			Create Questionnaire
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Box>
				<QuestionnaireForm root entityId={questionnaire.id} cache={cache} updateCache={updateCacheAndQuestionnaire}/>
			</Box>
		</DialogContent>
	</Dialog>
}

export interface CreateQuestionSetModalProps {
	onClose: () => void
}

export const CreateQuestionSetModal = (props: CreateQuestionSetModalProps) => {
	const [questionSet, setQuestionSet] = useState<QuestionSet>(instantiateQuestionSet())
	const [cache, updateCache] = useState<QuestionnaireDataCache>({
		...instantiateQuestionnaireCache(), questionSets: {[questionSet.id]: questionSet}, questionSetVersions: {[questionSet.versions[0].id]: questionSet.versions[0]}
	})

	const updateCacheAndQuestionSet = useCallback((newCache: QuestionnaireDataCache) => {
		updateCache(newCache)
		if (newCache.idMap[questionSet.id] && questionSet.id.startsWith("NEW")) {
			setQuestionSet(newCache.questionSets[newCache.idMap[questionSet.id]])
		}
	}, [questionSet, updateCache, setQuestionSet])

	return <Dialog open fullScreen slots={{transition: Transition}}>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			Create Question Group
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Box>
				<QuestionSetForm root entityId={questionSet.id} cache={cache} updateCache={updateCacheAndQuestionSet}/>
			</Box>
		</DialogContent>
	</Dialog>
}

export interface CreateQuestionModalProps {
	onClose: () => void
}

export const CreateQuestionModal = (props: CreateQuestionModalProps) => {
	const [question, setQuestion] = useState<Question>(instantiateQuestion())
	const [cache, updateCache] = useState<QuestionnaireDataCache>(populateCacheWithQuestions(instantiateQuestionnaireCache(), [question]))

	const updateCacheAndQuestion = useCallback((newCache: QuestionnaireDataCache) => {
		updateCache(newCache)
		if (newCache.idMap[question.id] && question.id.startsWith("NEW")) {
			setQuestion(newCache.questions[newCache.idMap[question.id]])
		}
	}, [question, updateCache, setQuestion])

	return <Dialog open fullScreen slots={{transition: Transition}}>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			Create Question
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Box>
				<QuestionForm root entityId={question.id} cache={cache} updateCache={updateCacheAndQuestion}/>
			</Box>
		</DialogContent>
	</Dialog>
}

export interface ViewQuestionModalProps {
	entityId: string
	onClose: () => void
}

export const ViewQuestionModal = (props: ViewQuestionModalProps) => {
	const [cache, updateCache] = useState<QuestionnaireDataCache>(instantiateQuestionnaireCache())
	const [question, setQuestion] = useState<Question | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)

	useEffect(() => {
		if (isLoaded == false && question == null) {
			getQuestion({question: props.entityId}, (data, resp, err) => {
				if (data) {
					setIsLoaded(true)
					setQuestion(data)
					updateCache(populateCacheWithQuestions(cache, [data]))
				}
			})
		}
	}, [isLoaded, setIsLoaded, setQuestion, updateCache, cache])

	return <Dialog open fullScreen slots={{transition: Transition}}>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			View Question
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Box>
				{isLoaded && question
					? <QuestionForm root entityId={question.id} cache={cache} updateCache={updateCache}/>
					: <CircularProgress />
				}
			</Box>
		</DialogContent>
	</Dialog>
}

export interface ViewQuestionSetModalProps {
	entityId: string
	onClose: () => void
}

export const ViewQuestionSetModal = (props: ViewQuestionModalProps) => {
	const [cache, updateCache] = useState<QuestionnaireDataCache>(instantiateQuestionnaireCache())
	const [questionSet, setQuestionSet] = useState<QuestionSet | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)

	useEffect(() => {
		if (isLoaded == false && questionSet == null) {
			getQuestionSet({questionSet: props.entityId}, (data, resp, err) => {
				if (data) {
					const updatedCache = populateCacheWithQuestionSets(cache, [data])
					const missing = getQuestionSetChildrenMissingFromCache(updatedCache, data)
					if (missing.length > 0) {
						retrieveQuestionData(missing, updatedCache, (newCache) => {
							updateCache(newCache)
							setIsLoaded(true)
							setQuestionSet(data)
						})
					} else {
						updateCache(updatedCache)
						setIsLoaded(true)
						setQuestionSet(data)
					}
				}
			})
		}
	}, [isLoaded, setIsLoaded, setQuestionSet, updateCache, cache])

	return <Dialog open fullScreen slots={{transition: Transition}}>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			View Question Group
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Box>
				{isLoaded && questionSet
					? <QuestionSetForm root entityId={questionSet.id} cache={cache} updateCache={updateCache}/>
					: <CircularProgress />
				}
			</Box>
		</DialogContent>
	</Dialog>
}

export interface ViewQuestionSetModalProps {
	entityId: string
	onClose: () => void
}

export const ViewQuestionnaireModal = (props: ViewQuestionModalProps) => {
	const [cache, updateCache] = useState<QuestionnaireDataCache>(instantiateQuestionnaireCache())
	const [questionSet, setQuestionnaire] = useState<Questionnaire | null>(null)
	const [isLoaded, setIsLoaded] = useState(false)

	useEffect(() => {
		if (isLoaded == false && questionSet == null) {
			getQuestionnaire({questionnaire: props.entityId}, (data, resp, err) => {
				if (data) {
					const updatedCache = populateCacheWithQuestionnaires(cache, [data])
					const missing = getQuestionnaireChildrenMissingFromCache(updatedCache, data)
					if (missing.length > 0) {
						retrieveQuestionData(missing, updatedCache, (newCache) => {
							updateCache(newCache)
							setIsLoaded(true)
							setQuestionnaire(data)
						})
					} else {
						updateCache(updatedCache)
						setIsLoaded(true)
						setQuestionnaire(data)
					}
				}
			})
		}
	}, [isLoaded, setIsLoaded, setQuestionnaire, updateCache, cache])

	return <Dialog open fullScreen slots={{transition: Transition}}>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			View Question Group
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Box>
				{isLoaded && questionSet
					? <QuestionnaireForm root entityId={questionSet.id} cache={cache} updateCache={updateCache}/>
					: <CircularProgress />
				}
			</Box>
		</DialogContent>
	</Dialog>
}

export interface DeleteQuestionnaireModalProps {
	onDelete: () => void
	onClose: () => void
}

export const DeleteQuestionnaireModal = (props: DeleteQuestionnaireModalProps) => {
	return <Dialog open>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			Delete Questionnaire
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Stack direction={'column'} spacing={2}>
				<Typography variant="body1">Are you sure you would like to delete this Questionnaire?</Typography>
				<Button variant='contained' onClick={props.onDelete}>Delete</Button>
			</Stack>
		</DialogContent>
	</Dialog>
}

export interface DeleteQuestionSetModalProps {
	onDelete: () => void
	onClose: () => void
}

export const DeleteQuestionSetModal = (props: DeleteQuestionSetModalProps) => {
	return <Dialog open>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			Delete Question Group
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Stack direction={'column'} spacing={2}>
				<Typography variant="body1">Are you sure you would like to delete this Question Group?</Typography>
				<Button variant='contained' onClick={props.onDelete}>Delete</Button>
			</Stack>
		</DialogContent>
	</Dialog>
}

export interface DeleteQuestionModalProps {
	onDelete: () => void
	onClose: () => void
}

export const DeleteQuestionModal = (props: DeleteQuestionModalProps) => {
	return <Dialog open>
		<DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
			Delete Question
			<IconButton onClick={props.onClose}>
				<Close />
			</IconButton>
		</DialogTitle>
		<DialogContent>
			<Stack direction={'column'} spacing={2}>
				<Typography variant="body1">Are you sure you would like to delete this Question?</Typography>
				<Button variant='contained' onClick={props.onDelete}>Delete</Button>
			</Stack>
		</DialogContent>
	</Dialog>
}

export interface QuestionnaireListProps {

}

export const QuestionnaireList = (props: QuestionnaireListProps) => {
	const navigate = useNavigate()
	const [orgQuestionnaires, setOrgQuestionnaires] = useState<Questionnaire[] | null>(null);
	const [isLoaded, setIsLoaded] = useState(false)
	const [shouldReload, setShouldReload] = useState(false)

	const orgId = useContext(OrganizationContext)
	if (orgId == null) {
		return <Typography variant="body2">Invalid organization, try logging out and back in?</Typography>
	}

	useEffect(() => {
		if (isLoaded && shouldReload || !isLoaded) {
			listQuestionnaires({organization: orgId}, (data, resp) => {
				if (data) {
					setOrgQuestionnaires(data);
					setIsLoaded(true);
				} else {
					setOrgQuestionnaires(null);
					setIsLoaded(false);
				}
				setShouldReload(false)
			});
		}
	}, [setOrgQuestionnaires, setIsLoaded, shouldReload, setShouldReload]);

	const [showViewEditModal, setShowViewEditModal] = useState<string | null>(null)

	const [showDeletePrompt, setShowDeletePrompt] = useState<string | null>(null)
	const onDelete = useCallback((questionnaireId: string) => {
		deleteQuestionnaire({questionnaire: questionnaireId}, () => {
			setShowDeletePrompt(null)
			setShouldReload(true)
		})
	}, [setShowDeletePrompt, setShouldReload])

	return (isLoaded && orgQuestionnaires)
		? <List>
			{orgQuestionnaires && orgQuestionnaires.length > 0 ? orgQuestionnaires.map((questionnaire: Questionnaire, idx) => {
				return (
					<ListItem key={questionnaire.id} divider={idx<orgQuestionnaires.length-1}>
						<Box width='100%' display='flex' justifyContent='space-between' alignItems='center'>
							<Stack direction='row' spacing={2} alignItems={'center'}>
								<Typography variant="h6">{questionnaire.name}</Typography>
							</Stack>
							<Stack direction='row' spacing={0} alignItems={'center'} sx={{ml: 1}}>
								{questionnaire.latest_version ? <Chip label={`Latest Version: ${questionnaire.versions.find((x) => x.id == questionnaire.latest_version)?.version}`} /> : <></>}
								{questionnaire.has_draft ? <Chip label={`Draft`} /> : <></>}
								<IconButton sx={{mr: -1}} onClick={() => setShowViewEditModal(questionnaire.id)}><EditDocument /></IconButton>
								<IconButton onClick={() => setShowDeletePrompt(questionnaire.id)}><DeleteForever /></IconButton>
							</Stack>
						</Box>
						{showViewEditModal == questionnaire.id ? <ViewQuestionnaireModal entityId={questionnaire.id} onClose={() => setShowViewEditModal(null)}></ViewQuestionnaireModal> : <></>}
						{showDeletePrompt == questionnaire.id ? <DeleteQuestionnaireModal onClose={() => setShowDeletePrompt(null)} onDelete={() => onDelete(questionnaire.id)}></DeleteQuestionnaireModal> : <></>}
					</ListItem>
				)
			}) : <Typography variant="body1">This organization has no questionnaires!</Typography>}
		</List>
		: <CircularProgress />
}

export interface QuestionSetListProps {

}

export const QuestionSetList = (props: QuestionSetListProps) => {
	const navigate = useNavigate()
	const [orgQuestionSets, setOrgQuestionSets] = useState<QuestionSet[] | null>(null);
	const [isLoaded, setIsLoaded] = useState(false)
	const [shouldReload, setShouldReload] = useState(false)

	const orgId = useContext(OrganizationContext)
	if (orgId == null) {
		return <Typography variant="body2">Invalid organization, try logging out and back in?</Typography>
	}

	useEffect(() => {
		if (shouldReload && isLoaded || !isLoaded) {
			listQuestionSets({organization: orgId}, (data, resp) => {
				if (data) {
					setOrgQuestionSets(data);
					setIsLoaded(true);
				} else {
					setOrgQuestionSets(null);
					setIsLoaded(false);
				}
				setShouldReload(false)
			});
		}
	}, [setOrgQuestionSets, setIsLoaded, shouldReload, setShouldReload]);

	const [showViewEditModal, setShowViewEditModal] = useState<string | null>(null)

	const [showDeletePrompt, setShowDeletePrompt] = useState<string | null>(null)
	const onDelete = useCallback((questionSetId: string) => {
		deleteQuestionSet({questionSet: questionSetId}, () => {
			setShowDeletePrompt(null)
			setShouldReload(true)
		})
	}, [setShowDeletePrompt, setShouldReload])

	return (isLoaded && orgQuestionSets)
		? <List>
			{orgQuestionSets && orgQuestionSets.length > 0 ? orgQuestionSets.map((questionSet: QuestionSet, idx) => {
				return (
					<ListItem key={questionSet.id} divider={idx<orgQuestionSets.length-1}>
						<Box width='100%' display='flex' justifyContent='space-between' alignItems='center'>
							<Stack direction='row' spacing={2} alignItems={'center'}>
								<Typography variant="h6">{questionSet.name}</Typography>
							</Stack>
							<Stack direction='row' spacing={0} alignItems={'center'} sx={{ml: 1}}>
								{questionSet.latest_version ? <Chip label={`Latest Version: ${questionSet.versions.find((x) => x.id == questionSet.latest_version)?.version}`} /> : <></>}
								{questionSet.has_draft ? <Chip label={`Draft`} /> : <></>}
								<IconButton sx={{mr: -1}} onClick={() => setShowViewEditModal(questionSet.id)}><EditDocument /></IconButton>
								<IconButton onClick={() => setShowDeletePrompt(questionSet.id)}><DeleteForever /></IconButton>
							</Stack>
						</Box>
						{showViewEditModal == questionSet.id ? <ViewQuestionSetModal entityId={questionSet.id} onClose={() => setShowViewEditModal(null)}></ViewQuestionSetModal> : <></>}
						{showDeletePrompt == questionSet.id ? <DeleteQuestionSetModal onClose={() => setShowDeletePrompt(null)} onDelete={() => onDelete(questionSet.id)}></DeleteQuestionSetModal> : <></>}
					</ListItem>
				)
			}) : <Typography variant="body1">This organization has no question sets!</Typography>}
		</List>
		: <CircularProgress />
}

export interface QuestionListProps {

}

export const QuestionList = (props: QuestionListProps) => {
	const [orgQuestions, setOrgQuestions] = useState<Question[] | null>(null);
	const [shouldReload, setShouldReload] = useState(false)
	const [isLoaded, setIsLoaded] = useState(false)

	const orgId = useContext(OrganizationContext)
	if (orgId == null) {
		return <Typography variant="body2">Invalid organization, try logging out and back in?</Typography>
	}

	useEffect(() => {
		if (shouldReload && isLoaded || !isLoaded) {
			listQuestions({organization: orgId}, (data, resp) => {
				if (data) {
					setOrgQuestions(data);
					setIsLoaded(true);
				} else {
					setOrgQuestions(null);
					setIsLoaded(false);
				}
				setShouldReload(false)
			});
		}
	}, [setOrgQuestions, setIsLoaded, setShouldReload, shouldReload, isLoaded]);

	const [showViewEditModal, setShowViewEditModal] = useState<string | null>(null)

	const [showDeletePrompt, setShowDeletePrompt] = useState<string | null>(null)
	const onDelete = useCallback((questionId: string) => {
		deleteQuestion({question: questionId}, () => {
			setShowDeletePrompt(null)
			setShouldReload(true)
		})
	}, [setShowDeletePrompt, setShouldReload])

	return (isLoaded && orgQuestions)
		? <List>
			{orgQuestions && orgQuestions.length > 0 ? orgQuestions.map((question: Question, idx) => {
				const latestVersion = question.versions.find((x) => x.id == question.latest_version)
				return (
					<ListItem key={question.id} divider={idx<orgQuestions.length-1}>
						<Box width='100%' display='flex' justifyContent='space-between' alignItems='center'>
							<Stack direction='row' spacing={2} alignItems={'center'}>
								<Typography variant="h6">{latestVersion?.text ?? question.versions.find((x) => x.status == QuestionAuthoringStatus.DRAFT)?.text ?? <Error />}</Typography>
							</Stack>
							<Stack direction='row' spacing={0} alignItems={'center'} sx={{ml: 1}}>
								{question.latest_version ? <Chip label={`Latest Version: ${latestVersion?.version}`} /> : <></>}
								{question.has_draft ? <Chip label={`Draft`} /> : <></>}
								<IconButton sx={{mr: -1}} onClick={() => setShowViewEditModal(question.id)}><EditDocument /></IconButton>
								<IconButton onClick={() => setShowDeletePrompt(question.id)}><DeleteForever /></IconButton>
							</Stack>
						</Box>
						{showViewEditModal == question.id ? <ViewQuestionModal entityId={question.id} onClose={() => setShowViewEditModal(null)}></ViewQuestionModal> : <></>}
						{showDeletePrompt == question.id ? <DeleteQuestionModal onClose={() => setShowDeletePrompt(null)} onDelete={() => onDelete(question.id)}></DeleteQuestionModal> : <></>}
					</ListItem>
				)
			}) : <Typography variant="body1">This organization has no questions!</Typography>}
		</List>
		: <CircularProgress />
}

export class ManageQuestionnairesProps {
	org: string;

	constructor(org: string) {
		this.org = org;
	}
}

export const ManageQuestionnaires = (props: ManageQuestionnairesProps) => {
	const [showCreateQuestionnaire, setShowCreateQuestionnaire] = useState(false)
	const [showCreateQuestionSet, setShowCreateQuestionSet] = useState(false)
	const [showCreateQuestion, setShowCreateQuestion] = useState(false)

	return (<>
		<Paper sx={{m:1, p:2}}>
			<Stack direction='row' width={'100%'}>
				<Typography variant="h5">Questionnaires</Typography>
				<Button variant="contained" onClick={() => setShowCreateQuestionnaire(true)} sx={{ml: 'auto'}} startIcon={<Add/>}>Create a new questionnaire</Button>
				{showCreateQuestionnaire
					? <CreateQuestionnaireModal onClose={() => setShowCreateQuestionnaire(false)}/>
					: <></>
				}
			</Stack>
			<QuestionnaireList />
		</Paper>
		<Paper sx={{m:1, p:2}}>
			<Stack direction='row' width={'100%'}>
				<Typography variant="h5">Question Groups</Typography>
				<Button variant="contained" onClick={() => setShowCreateQuestionSet(true)} sx={{ml: 'auto'}} startIcon={<Add/>}>Create a new question group</Button>
				{showCreateQuestionSet
					? <CreateQuestionSetModal onClose={() => setShowCreateQuestionSet(false)}/>
					: <></>
				}
			</Stack>
			<QuestionSetList />
		</Paper>
		<Paper sx={{m:1, p:2}}>
			<Stack direction='row' width={'100%'}>
				<Typography variant="h5">Questions</Typography>
				<Button variant="contained" onClick={() => setShowCreateQuestion(true)} sx={{ml: 'auto'}} startIcon={<Add/>}>Create a new question</Button>
				{showCreateQuestion
					? <CreateQuestionModal onClose={() => setShowCreateQuestion(false)}/>
					: <></>
				}
			</Stack>
			<QuestionList />
		</Paper>
	</>)
}
